import * as React from 'react';
import { Drawer as DrawerPrimitive } from '@base-ui/react/drawer';
import { XIcon } from '@phosphor-icons/react/X';
import { cn } from '../../../utils/cn';
import { Button } from '../button';

interface DrawerContextProps {
  /**
   * The root was given at least one snap point.
   */
  hasSnapPoints: boolean;

  /**
   * As the root's `modal`; only a modal drawer draws the overlay.
   */
  modal: DrawerPrimitive.Root.Props['modal'];

  /**
   * The popup draws the swipe handle above its content.
   */
  showSwipeHandle: boolean;

  /**
   * The edge the drawer leaves by; `down` and `up` swipe on the y axis.
   */
  swipeDirection: NonNullable<DrawerPrimitive.Root.Props['swipeDirection']>;
}

const DrawerContext = React.createContext<DrawerContextProps | null>(null);

function useDrawer() {
  const context = React.useContext(DrawerContext);

  if (!context) throw new Error('useDrawer must be used within a Drawer.');

  return context;
}

export interface DrawerProps extends DrawerPrimitive.Root.Props {
  /**
   * Draws the handle at the top; the phone sheet always shows one, so it is on
   * unless a sheet opts out.
   *
   * @default true
   */
  showSwipeHandle?: boolean;
}

function Drawer({
  modal = true,
  showSwipeHandle = true,
  snapPoints,
  swipeDirection = 'down',
  ...props
}: DrawerProps) {
  const hasSnapPoints = snapPoints != null && snapPoints.length > 0;
  const contextValue = React.useMemo(
    () => ({ hasSnapPoints, modal, showSwipeHandle, swipeDirection }),
    [hasSnapPoints, modal, showSwipeHandle, swipeDirection],
  );

  return (
    <DrawerContext.Provider value={contextValue}>
      <DrawerPrimitive.Root
        data-slot="drawer"
        modal={modal}
        snapPoints={snapPoints}
        swipeDirection={swipeDirection}
        {...props}
      />
    </DrawerContext.Provider>
  );
}

function DrawerTrigger(props: DrawerPrimitive.Trigger.Props) {
  return (
    <DrawerPrimitive.Trigger
      data-slot="drawer-trigger"
      {...props}
    />
  );
}

function DrawerPortal(props: DrawerPrimitive.Portal.Props) {
  return (
    <DrawerPrimitive.Portal
      data-slot="drawer-portal"
      {...props}
    />
  );
}

function DrawerClose(props: DrawerPrimitive.Close.Props) {
  return (
    <DrawerPrimitive.Close
      data-slot="drawer-close"
      {...props}
    />
  );
}

/**
 * Wraps a `DrawerContent` that holds form fields. iOS Safari does not resize
 * the page for its keyboard, so the provider measures it and sets
 * `--drawer-keyboard-inset`, which the popup pads its foot by.
 */
const DrawerKeyboard = DrawerPrimitive.VirtualKeyboardProvider;

function DrawerOverlay({ className, ...props }: DrawerPrimitive.Backdrop.Props) {
  const classes = {
    overlay: cn(
      'bg-scrim fixed inset-0 z-50 min-h-dvh opacity-[max(var(--drawer-overlay-min-opacity,0),calc(1-var(--drawer-swipe-progress)))] transition-opacity duration-450 ease-[cubic-bezier(0.32,0.72,0,1)] select-none data-ending-style:pointer-events-none data-ending-style:opacity-0 data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-snap-points:[--drawer-overlay-min-opacity:0.5] data-starting-style:opacity-0 data-swiping:duration-0 supports-[-webkit-touch-callout:none]:absolute',
      className,
    ),
  };

  return (
    <DrawerPrimitive.Backdrop
      className={classes.overlay}
      data-slot="drawer-overlay"
      {...props}
    />
  );
}

function DrawerSwipeHandle({ className, ...props }: React.ComponentProps<'div'>) {
  const classes = {
    handle: cn(
      'after:bg-hairline-strong relative z-10 flex shrink-0 cursor-grab transition-opacity duration-200 group-data-nested-drawer-open/drawer-popup:opacity-0 group-data-nested-drawer-swiping/drawer-popup:opacity-100 group-data-[swipe-axis=x]/drawer-popup:h-full group-data-[swipe-axis=x]/drawer-popup:w-3 group-data-[swipe-axis=x]/drawer-popup:items-center group-data-[swipe-axis=y]/drawer-popup:h-3.25 group-data-[swipe-axis=y]/drawer-popup:w-full group-data-[swipe-axis=y]/drawer-popup:justify-center group-data-[swipe-direction=down]/drawer-popup:items-end group-data-[swipe-direction=left]/drawer-popup:order-last group-data-[swipe-direction=left]/drawer-popup:justify-start group-data-[swipe-direction=right]/drawer-popup:justify-end group-data-[swipe-direction=up]/drawer-popup:order-last group-data-[swipe-direction=up]/drawer-popup:items-start after:block after:shrink-0 after:rounded-sm group-data-[swipe-axis=x]/drawer-popup:after:h-24 group-data-[swipe-axis=x]/drawer-popup:after:w-1 group-data-[swipe-axis=y]/drawer-popup:after:h-1.25 group-data-[swipe-axis=y]/drawer-popup:after:w-9 active:cursor-grabbing',
      className,
    ),
  };

  return (
    <div
      aria-hidden="true"
      className={classes.handle}
      data-slot="drawer-swipe-handle"
      {...props}
    />
  );
}

export interface DrawerContentProps extends DrawerPrimitive.Popup.Props {
  /**
   * `round` is the 36px close with a hover behind it, for a big window; `plain`
   * is the small ghost button.
   *
   * @default 'plain'
   */
  closeShape?: 'plain' | 'round';

  /**
   * Classes for the scrolling body inside the popup, where a padding goes so a
   * child can bleed to the popup's edges with a negative margin.
   */
  contentClassName?: string;

  /**
   * A window caps at `100dvh - 24px`, a popover (menu, inbox, wallet) at
   * `100dvh - 48px`.
   *
   * @default 'window'
   */
  height?: 'popover' | 'window';

  /**
   * Marks the body as a sheet (`data-sheet`), so controls inside restyle for it
   * with `in-data-sheet:` as they do in the bottom Sheet. The close button sits
   * outside the body and is not restyled.
   *
   * @default false
   */
  isSheet?: boolean;

  /**
   * Draws the close button at the top right, staying put as the body scrolls;
   * off for a sheet that cannot be closed or a menu.
   *
   * @default true
   */
  showCloseButton?: boolean;
}

function DrawerContent({
  children,
  className,
  closeShape = 'plain',
  contentClassName,
  height = 'window',
  isSheet = false,
  showCloseButton = true,
  ...props
}: DrawerContentProps) {
  const { hasSnapPoints, modal, showSwipeHandle, swipeDirection } = useDrawer();
  const swipeAxis = swipeDirection === 'down' || swipeDirection === 'up' ? 'y' : 'x';
  const snapPointsAttribute = hasSnapPoints ? '' : undefined;
  const isHandleAbove = showSwipeHandle && swipeDirection === 'down';
  const isRoundClose = closeShape === 'round';
  const sheetAttribute = isSheet ? '' : undefined;

  const classes = {
    popup: cn(
      // Base.
      'group/drawer-popup bg-surface-1 text-fg-1 data-[swipe-direction=down]:rounded-t-sheet pointer-events-auto fixed z-50 m-(--drawer-inset,0px) flex h-(--drawer-content-height) max-h-(--drawer-content-max-height,none) min-h-0 w-(--drawer-content-width,auto) transform-[translate3d(var(--translate-x,0px),var(--translate-y,0px),0)_scale(var(--stack-scale))] flex-col text-sm transition-[transform,height,opacity,filter] duration-450 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform outline-none select-none [interpolate-size:allow-keywords] data-[swipe-direction=left]:rounded-r-xl data-[swipe-direction=left]:border-r data-[swipe-direction=right]:rounded-l-xl data-[swipe-direction=right]:border-l data-[swipe-direction=up]:rounded-b-xl data-[swipe-direction=up]:border-b',
      // Foot: the home indicator, or the keyboard when it is taller.
      'pb-[max(env(safe-area-inset-bottom),var(--drawer-keyboard-inset,0px))]',
      // Nested.
      'data-nested-drawer-open:overflow-hidden data-nested-drawer-open:brightness-95',
      // Bleed.
      'after:pointer-events-none after:absolute after:bg-(--drawer-bleed-background,var(--color-surface-1)) data-[swipe-axis=x]:after:inset-y-0 data-[swipe-axis=x]:after:w-(--bleed) data-[swipe-axis=y]:after:inset-x-0 data-[swipe-axis=y]:after:h-(--bleed) data-[swipe-direction=down]:after:top-full data-[swipe-direction=left]:after:right-full data-[swipe-direction=right]:after:left-full data-[swipe-direction=up]:after:bottom-full',
      // Sizing.
      '[--drawer-content-height:var(--drawer-height,auto)] data-[swipe-axis=x]:[--drawer-content-width:75%] data-[swipe-axis=y]:data-snap-points:[--drawer-content-height:100dvh] data-[swipe-axis=x]:sm:[--drawer-content-width:24rem]',
      {
        'data-[swipe-axis=y]:[--drawer-content-max-height:calc(100dvh-1.5rem)]':
          height === 'window',
        'data-[swipe-axis=y]:[--drawer-content-max-height:calc(100dvh-3rem)]': height === 'popover',
      },
      // Stack.
      '[--bleed:3rem] [--peek:1rem] [--stack-height:var(--drawer-frontmost-height,var(--drawer-height,0px))] [--stack-peek-offset:max(0px,calc((var(--nested-drawers)-var(--stack-progress))*var(--peek)))] [--stack-progress:clamp(0,var(--drawer-swipe-progress),1)] [--stack-scale-base:max(0,calc(1-(var(--nested-drawers)*var(--stack-step))))] [--stack-scale:clamp(0,calc(var(--stack-scale-base)+(var(--stack-step)*var(--stack-progress))),1)] [--stack-shrink:calc(1-var(--stack-scale))] [--stack-step:0.05]',
      // Transitions.
      'data-ending-style:transform-(--closed-transform) data-ending-style:opacity-[0.9999] data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-nested-drawer-swiping:duration-0 data-ending-style:data-nested-drawer-swiping:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-starting-style:transform-(--closed-transform) data-swiping:duration-0 data-ending-style:data-swiping:duration-[calc(var(--drawer-swipe-strength)*400ms)]',
      // Axis: y.
      'data-[swipe-axis=y]:inset-x-0 data-[swipe-axis=y]:data-nested-drawer-open:h-(--stack-height)',
      // Axis: x.
      'data-[swipe-axis=x]:inset-y-0 data-[swipe-axis=x]:flex-row',
      // Direction: down.
      'data-[swipe-direction=down]:bottom-0 data-[swipe-direction=down]:origin-bottom data-[swipe-direction=down]:[--closed-transform:translate3d(0,calc(100%+var(--drawer-inset,0px)+2px),0)] data-[swipe-direction=down]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)-var(--stack-peek-offset)-(var(--stack-shrink)*var(--stack-height)))]',
      // Direction: up.
      'data-[swipe-direction=up]:top-0 data-[swipe-direction=up]:origin-top data-[swipe-direction=up]:[--closed-transform:translate3d(0,calc(-100%-var(--drawer-inset,0px)-2px),0)] data-[swipe-direction=up]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y)+var(--stack-peek-offset)+(var(--stack-shrink)*var(--stack-height)))]',
      // Direction: left.
      'data-[swipe-direction=left]:left-0 data-[swipe-direction=left]:origin-left data-[swipe-direction=left]:[--closed-transform:translate3d(calc(-100%-var(--drawer-inset,0px)-2px),0,0)] data-[swipe-direction=left]:[--translate-x:calc(var(--drawer-swipe-movement-x)+var(--stack-peek-offset)+(var(--stack-shrink)*100%))]',
      // Direction: right.
      'data-[swipe-direction=right]:right-0 data-[swipe-direction=right]:origin-right data-[swipe-direction=right]:[--closed-transform:translate3d(calc(100%+var(--drawer-inset,0px)+2px),0,0)] data-[swipe-direction=right]:[--translate-x:calc(var(--drawer-swipe-movement-x)-var(--stack-peek-offset)-(var(--stack-shrink)*100%))]',
      className,
    ),
    content: cn(
      'flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain rounded-[inherit] transition-opacity duration-300 ease-[cubic-bezier(0.45,1.005,0,1.005)] select-text group-data-nested-drawer-open/drawer-popup:opacity-0 group-data-nested-drawer-swiping/drawer-popup:opacity-100 group-data-swiping/drawer-popup:select-none',
      contentClassName,
    ),
    // Plain: centred on the title's 28px line, under the header's 12px padding and the 13px handle.
    // Round: centred on a 20px title line under the body's 28px padding, with the handle's 13px or without.
    close: cn('absolute z-3', {
      'right-3': !isRoundClose,
      'top-2': !isRoundClose && !isHandleAbove,
      'top-5.25': !isRoundClose && isHandleAbove,
      'focus-visible:ring-ring hover:bg-surface-2 right-3.5 grid size-9 place-items-center rounded-full transition-colors focus-visible:ring-2':
        isRoundClose,
      'top-5': isRoundClose && !isHandleAbove,
      'top-8': isRoundClose && isHandleAbove,
    }),
  };

  const renderOverlay = () => {
    if (modal !== true) return null;
    return <DrawerOverlay data-snap-points={snapPointsAttribute} />;
  };

  const renderSwipeHandle = () => {
    if (!showSwipeHandle) return null;
    return <DrawerSwipeHandle />;
  };

  const renderCloseButton = () => {
    if (!showCloseButton) return null;
    if (isRoundClose)
      return (
        <DrawerClose
          aria-label="Close"
          className={classes.close}
        >
          <XIcon
            aria-hidden
            className="size-4"
            weight="bold"
          />
        </DrawerClose>
      );
    return (
      <DrawerClose
        aria-label="Close"
        className={classes.close}
        render={
          <Button
            size="icon"
            variant="ghost"
          />
        }
      >
        <XIcon
          aria-hidden
          weight="bold"
        />
      </DrawerClose>
    );
  };

  return (
    <DrawerPortal>
      {renderOverlay()}
      <DrawerPrimitive.Viewport
        className="pointer-events-none fixed inset-0 z-50 select-none data-[modal=true]:pointer-events-auto"
        data-modal={modal}
        data-slot="drawer-viewport"
      >
        <DrawerPrimitive.Popup
          className={classes.popup}
          data-slot="drawer-popup"
          data-snap-points={snapPointsAttribute}
          data-swipe-axis={swipeAxis}
          {...props}
        >
          {renderSwipeHandle()}
          <DrawerPrimitive.Content
            className={classes.content}
            data-sheet={sheetAttribute}
            data-slot="drawer-content"
          >
            {children}
          </DrawerPrimitive.Content>
          {renderCloseButton()}
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
  const classes = {
    header: cn('bg-surface-1 sticky top-0 z-2 flex shrink-0 flex-col gap-0.5 px-4 py-3', className),
  };

  return (
    <div
      className={classes.header}
      data-slot="drawer-header"
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  const classes = {
    footer: cn(
      'border-hairline bg-surface-1 sticky bottom-0 z-2 mt-auto flex shrink-0 flex-row gap-2 border-t px-4 py-3 *:h-12 *:min-w-0 *:flex-1',
      className,
    ),
  };

  return (
    <div
      className={classes.footer}
      data-slot="drawer-footer"
      {...props}
    />
  );
}

function DrawerTitle({ className, ...props }: DrawerPrimitive.Title.Props) {
  const classes = {
    title: cn('text-fg-1 truncate pr-10 text-lg font-semibold', className),
  };

  return (
    <DrawerPrimitive.Title
      className={classes.title}
      data-slot="drawer-title"
      {...props}
    />
  );
}

function DrawerDescription({ className, ...props }: DrawerPrimitive.Description.Props) {
  const classes = {
    description: cn('text-fg-2 text-sm text-balance', className),
  };

  return (
    <DrawerPrimitive.Description
      className={classes.description}
      data-slot="drawer-description"
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerSwipeHandle,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  DrawerKeyboard,
};
